import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GmonPlanningResults } from 'src/app/_models/gmonResults';
import { GPlanInput } from 'src/app/_models/gplan-input';
import { TopologyList, TopologyWithRoot } from 'src/app/_models/topology-list';
import { ApplicationsResultsService } from 'src/app/_services/applications-results-service/applications-results.service';
import { CommunicationService } from 'src/app/_services/communication-service/communication.service';
import { GPlanService } from 'src/app/_services/gplan-service/gplan.service';

@Component({
  selector: 'app-gmon-planning',
  templateUrl: './gmon-planning.component.html',
  styleUrls: ['./gmon-planning.component.css']
})
export class GmonPlanningComponent implements OnInit {

  @ViewChild('searchInput', { static: false}) searchInputRef!: ElementRef;
  private shouldFocusInput = false;

  public isSidebarOpen: boolean;
  public topologies: TopologyList;
  public selectTopologyMap: Map<string, string> = new Map<string, string>();
  public selectDisplayNameOfTopology: string = "";
  public gmonPlanningResults: Array<GmonPlanningResults> = new Array<GmonPlanningResults>();
  public selectedTopology: string;

  isSearching: boolean[] = Array(6).fill(false);
  searchText: string[] = Array(6).fill('');

  constructor(
    private communicationService: CommunicationService,
    private applicationResultsService: ApplicationsResultsService,
    private translate: TranslateService,
    private gplanService: GPlanService
  ) { 
    const lang = localStorage.getItem('Language');
    this.translate.use(lang);
  }

  ngOnInit() {
    this.topologies = JSON.parse(localStorage.getItem("topologies"));
    this.loadTopologies();
    // console.log('Topology Map:', this.selectTopologyMap);

    this.loadGmonPlanningResults(this.getTopologiesDisplayNameForSelectField()[0]);

    this.applicationResultsService.setSidebarOpen(true);
    this.isSidebarOpen = this.applicationResultsService.getSidebarOpen();
  }

  loadGmonPlanningResults(value: string) {
    this.applicationResultsService.getGmonPlanninResults(this.selectTopologyMap.get(value) || value).subscribe((data) => {
      
      this.getListOfScenariosForSelectedTopology(this.selectTopologyMap.get(value) || value).subscribe((scenariosMap: Map<number, string>) => {
        this.gmonPlanningResults = (data || []).filter(item => item).map(item => new GmonPlanningResults(
          item.gridElement_id  || "N/A",
          item.dso_internal_id  || "N/A",
          item.type  || "N/A",
          item.maxVoltage  || "N/A",
          item.minVoltage || "N/A",
          item.scenarioId ? item.scenarioId + " - " + scenariosMap.get(item.scenarioId) || "N/A" : "N/A",
          item.maxVoltageTimestamp || "N/A",
          item.minVoltageTimestamp || "N/A",
          item.numberOfUpperBoundViolation || "N/A",
          item.numberOfLowerBoundViolation || "N/A"
        ));
      })
        
    });
    // console.log("GMON Planning Results: ", this.gmonPlanningResults);
  }

  /**
   * This function retrieves the list of scenarios for a given topology ID
   * It returns an Observable that emits a Map of scenario IDs and their names
   */
  getListOfScenariosForSelectedTopology(topologyId: string): Observable<Map<number, string>> {
    return this.gplanService.getListAvailableScenariosForOverallResults(topologyId).pipe(
      map((data: Array<GPlanInput>) => {
        const scenariosMap = new Map<number, string>();
        data.forEach((scenario: GPlanInput) => {
          scenariosMap.set(scenario.id, scenario.name);
        });
        return scenariosMap;
      })
    );
  }
  
  resetDefaultTopology(){
    this.communicationService.setTopologyId(null);
  }
  
  loadTopologies(){
    this.topologies.topologiesWithRoots.forEach((TopologyWithRoot: TopologyWithRoot) => {
      this.selectTopologyMap.set(TopologyWithRoot.displayName || TopologyWithRoot.internalDsoId + " - "  + TopologyWithRoot.TopologyId, TopologyWithRoot.TopologyId );
    })
    
    this.selectTopologyMap.forEach((value, key) => {
      if (key === this.topologies.topologiesWithRoots[0].displayName || key === this.topologies.topologiesWithRoots[0].internalDsoId + " - "  + this.topologies.topologiesWithRoots[0].TopologyId) {
        this.selectedTopology = key;
      }
    });

    // console.log("Selected Topology: ", this.selectedTopology);
  }

  getTopologiesDisplayNameForSelectField(): string[] {
    return Array.from(this.selectTopologyMap.keys());
  }

  sortTable(columnIndex: number): void {
    const table = document.getElementById("sortableTable");
    if (!table) return;
  
    const rows = Array.from((table as HTMLTableElement).rows).slice(1); // Skip header
    let isAscending = table.getAttribute("data-sort-order") === "asc";
  
    const parseDateToTimestamp = (value: string): number => {
      const trimmed = value.trim();
      const date = new Date(trimmed);
      return isNaN(date.getTime()) ? Infinity : date.getTime();
    };
  
    const sampleSize = Math.min(5, rows.length);
    const isDate = rows.slice(0, sampleSize).some(row => {
      const value = row.cells[columnIndex].innerText.trim();
      if (
        value === '' ||
        value.toLowerCase() === 'n/a' ||
        !/[\/\-:,]/.test(value)
      ) {
        return false;
      }
      const date = new Date(value);
      return !isNaN(date.getTime());
    });
    const isNumeric = rows.some(row => {
      const value = row.cells[columnIndex].innerText.trim();
      return !isNaN(parseFloat(value)) && value !== '' && value !== 'N/A' && value !== 'n/a';
    });
  
    rows.sort((rowA, rowB) => {
      const cellA = rowA.cells[columnIndex].innerText.trim() || '';
      const cellB = rowB.cells[columnIndex].innerText.trim() || '';

      if (isDate) {
        const dateA = parseDateToTimestamp(cellA);
        const dateB = parseDateToTimestamp(cellB);
        // console.log("Date sorting");
        return isAscending ? dateA - dateB : dateB - dateA;
      }

      
      if (isNumeric) {
        const numbA = (cellA === 'N/A' || cellA === '' || isNaN(parseFloat(cellA))) ? Infinity : parseFloat(cellA);
        const numbB = (cellB === 'N/A' || cellB === '' || isNaN(parseFloat(cellB))) ? Infinity : parseFloat(cellB);
  
        // console.error("Numeric sorting");
        return isAscending ? numbA - numbB : numbB - numbA;
      } 
  
      return isAscending ? cellA.localeCompare(cellB) : cellB.localeCompare(cellA);
    });
    table.setAttribute("data-sort-order", isAscending ? "desc" : "asc");
    rows.forEach(row => table.appendChild(row));
  }

  toggleSearch(index: number): void {
    this.isSearching = this.isSearching.map((_, i) => i === index ? !this.isSearching[i] : false);

    if(this.isSearching.every(value => value === false)){
      this.searchText = Array(6).fill('');
    }
    // console.log("Search Toggled:", this.isSearching);

    
    if(this.isSearching[index]){
      this.shouldFocusInput = true;
    }
  }
  
  ngAfterViewChecked(){
    if(this.shouldFocusInput && this.searchInputRef){
      this.searchInputRef.nativeElement.focus();
      this.shouldFocusInput = false;
    }
  }

  filteredGmonPlanningResults(): any[] {
    
    if(this.isSearching.every(value => value === false)){
      this.searchText = Array(6).fill('');
    }
    if(this.searchText.every(text => !text)){
      return this.gmonPlanningResults;
    }

    return this.gmonPlanningResults.filter(item => {
      return this.searchText.every((text, index) => {
        if(!text) return true;
        const value = Object.values(item)[index];
        return value.toString().toLowerCase().includes(text.toLowerCase())
      });
    });
  }

  getViolationColoring(gmonResult: GmonPlanningResults): string {
    if(gmonResult.numberOfUpperBoundViolation > 0 || gmonResult.numberOfLowerBoundViolation > 0) {
      return 'red';
    }
  }
}
